import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { buildApiHeaders } from '../api/client';
import { performSessionEndLogout } from '../auth/idleLogoutEvents';
import { registerSessionUnauthorizedHandler } from '../auth/sessionEvents';

export const LOGIN_INVALID_CREDENTIALS_MESSAGE = 'Incorrect username or password';

function parseLoginErrorMessage(status: number, bodyText: string): string {
  if (status === 401) {
    return LOGIN_INVALID_CREDENTIALS_MESSAGE;
  }
  try {
    const parsed = JSON.parse(bodyText) as { detail?: unknown };
    const detail = parsed?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      const lower = detail.toLowerCase();
      if (
        lower.includes('invalid username') ||
        lower.includes('invalid password') ||
        lower.includes('incorrect username') ||
        lower.includes('incorrect password')
      ) {
        return LOGIN_INVALID_CREDENTIALS_MESSAGE;
      }
      return detail;
    }
  } catch {
    // not JSON
  }
  const trimmed = bodyText.trim();
  if (!trimmed) {
    return status >= 500 ? 'Server error. Please try again later.' : LOGIN_INVALID_CREDENTIALS_MESSAGE;
  }
  const lower = trimmed.toLowerCase();
  if (lower.includes('invalid username') || lower.includes('invalid password')) {
    return LOGIN_INVALID_CREDENTIALS_MESSAGE;
  }
  return trimmed;
}

const AUTH_STORAGE_KEY = 'intellicare_auth';

export interface AuthAccess {
  can_open_settings: boolean;
  can_open_test_dhis2: boolean;
  can_open_admin_portal: boolean;
  allowed_main_tabs: string[] | null;
  allowed_config_ids: string[] | null;
  config_scope_restricted: boolean;
}

interface AuthUser {
  id: number;
  username: string;
  displayName?: string;
  role?: string;
  doctor_id?: number | null;
}

interface AuthContextType {
  /** true once localStorage + /api/auth/me bootstrap pass has finished (success or failure) */
  bootstrapped: boolean;

  isAuthenticated: boolean;
  user: AuthUser | null;
  /** Part A: RBAC flags + allowed configs from `/api/auth/me` and login. */
  access: AuthAccess | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  /** Idle timeout or expired JWT — clears auth and shows login with a message. */
  logoutDueToSessionEnd: (reason: 'idle' | 'unauthorized') => void;

  /** Force-refresh identity/access from /api/auth/me (used after UAM changes). */
  refreshMe: () => Promise<void>;

  authError: string | null;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function normalizeAccess(raw: unknown): AuthAccess | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  return {
    can_open_settings: a.can_open_settings !== false,
    can_open_test_dhis2: a.can_open_test_dhis2 !== false,
    can_open_admin_portal: a.can_open_admin_portal !== false,
    allowed_main_tabs: Array.isArray(a.allowed_main_tabs) ? (a.allowed_main_tabs as string[]) : null,
    allowed_config_ids: Array.isArray(a.allowed_config_ids) ? (a.allowed_config_ids as string[]) : null,
    config_scope_restricted: Boolean(a.config_scope_restricted),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [access, setAccess] = useState<AuthAccess | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const [bootstrapped, setBootstrapped] = useState(false); // true once localStorage + /api/auth/me bootstrap pass has finished (success or failure)

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const logout = useCallback(() => {
    setUser(null);
    setAccess(null);
    setIsAuthenticated(false);
    setAuthError(null);
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const logoutDueToSessionEnd = useCallback((reason: 'idle' | 'unauthorized') => {
    setUser(null);
    setAccess(null);
    setIsAuthenticated(false);
    setAuthError(
      reason === 'idle'
        ? 'Your session ended due to inactivity. Please sign in again.'
        : 'Your session has expired. Please sign in again.',
    );
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const logoutDueToSessionEndRef = useRef(logoutDueToSessionEnd);
  logoutDueToSessionEndRef.current = logoutDueToSessionEnd;

  useEffect(
    () =>
      registerSessionUnauthorizedHandler(() =>
        performSessionEndLogout((reason) => logoutDueToSessionEndRef.current(reason), 'unauthorized'),
      ),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    // Bootstrapping completion fix (updated on 2026-04-08): always mark bootstrapped=true.
    void (async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw) as { token: string; user: AuthUser; access?: AuthAccess | null };
        if (!(data?.token && data?.user?.username)) return;
        if (cancelled) return;

        setUser(data.user);
        setAccess(normalizeAccess(data.access) ?? null);
        setIsAuthenticated(true);

        const resp = await fetch('/api/auth/me', { headers: buildApiHeaders() });
        if (cancelled) return;
        if (!resp.ok) {
          logoutRef.current();
          return;
        }
        try {
          const me = (await resp.json()) as { user?: AuthUser; access?: unknown };
          if (me?.user?.username) {
            const u: AuthUser = {
              id: me.user.id,
              username: me.user.username,
              displayName: me.user.displayName || me.user.username,
              role: me.user.role,
              doctor_id: me.user.doctor_id ?? null,
            };
            setUser(u);
            const nextAccess = normalizeAccess(me.access);
            setAccess(nextAccess);
            try {
              const prev = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || '{}') as {
                token?: string;
              };
              if (prev.token) {
                localStorage.setItem(
                  AUTH_STORAGE_KEY,
                  JSON.stringify({ token: prev.token, user: u, access: nextAccess }),
                );
              }
            } catch {
              // ignore
            }
          }
        } catch {
          // ignore parse errors
        }
      } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    const u = (username || '').trim();
    const p = (password || '').trim();
    setAuthError(null);
    if (!u || !p) {
      setAuthError('Username and password are required.');
      return false;
    }
    try {
      const body = new URLSearchParams();
      body.append('username', u);
      body.append('password', p);
      body.append('grant_type', '');
      const resp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        setAuthError(parseLoginErrorMessage(resp.status, errText));
        return false;
      }
      const data = (await resp.json()) as {
        access_token: string;
        token_type: string;
        user: AuthUser;
        access?: unknown;
      };
      if (!data?.access_token || !data?.user?.username) {
        setAuthError('Invalid response from server.');
        return false;
      }
      const authUser: AuthUser = {
        id: data.user.id,
        username: data.user.username,
        displayName: data.user.displayName || data.user.username,
        role: data.user.role,
        doctor_id: data.user.doctor_id ?? null,
      };
      const nextAccess = normalizeAccess(data.access);
      setUser(authUser);
      setAccess(nextAccess);
      setIsAuthenticated(true);
      try {
        localStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify({ token: data.access_token, user: authUser, access: nextAccess }),
        );
      } catch {
        // ignore
      }
      return true;
    } catch (e) {
      setAuthError('Login failed. Please try again.');
      return false;
    }
  }, []);

  // Force-refresh identity/access from /api/auth/me (updated on 2026-04-08).
  const refreshMe = useCallback(async (): Promise<void> => {
    const resp = await fetch('/api/auth/me', { headers: buildApiHeaders() });
    if (!resp.ok) {
      logoutRef.current();
      return;
    }
    const me = (await resp.json()) as { user?: AuthUser; access?: unknown };
    if (!me?.user?.username) return;
  
    const u: AuthUser = {
      id: me.user.id,
      username: me.user.username,
      displayName: me.user.displayName || me.user.username,
      role: me.user.role,
      doctor_id: me.user.doctor_id ?? null,
    };
    const nextAccess = normalizeAccess(me.access);
    setUser(u);
    setAccess(nextAccess);
    setIsAuthenticated(true);
  
    try {
      const prev = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || '{}') as { token?: string };
      if (prev.token) {
        localStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify({ token: prev.token, user: u, access: nextAccess }),
        );
      }
    } catch {}
  }, []);
  /** End of Force-refresh identity/access from /api/auth/me (used after UAM changes). added on 08042026 */
  
  return (
    <AuthContext.Provider
      value={{
        bootstrapped,
        isAuthenticated,
        user,
        access,
        login,
        logout,
        logoutDueToSessionEnd,
        refreshMe,
        authError,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
